import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MapIcon from '@mui/icons-material/Map';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isToday, addMonths, subMonths } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const getZone = (seatNum) => {
  if (seatNum >= 1 && seatNum <= 74) return 'Zone A';
  if (seatNum >= 75 && seatNum <= 113) return 'Zone B';
  if (seatNum >= 114 && seatNum <= 181) return 'Zone C';
  return '';
};

const buildCalendarGrid = (month) => {
  const firstDay = startOfMonth(month);
  const lastDay = endOfMonth(month);
  const days = eachDayOfInterval({ start: firstDay, end: lastDay });
  const startOffset = (getDay(firstDay) + 6) % 7; // Mon-first
  const grid = [...Array(startOffset).fill(null), ...days];
  while (grid.length % 7 !== 0) grid.push(null);
  return grid;
};

const MyBookings = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('calendar');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [calendarMonth, setCalendarMonth] = useState(startOfMonth(new Date()));
  const [releaseDialog, setReleaseDialog] = useState({ open: false, booking: null });
  const [releaseDayDialog, setReleaseDayDialog] = useState({ open: false, date: null, bookingIds: [] });
  const [dayDetailDialog, setDayDetailDialog] = useState({ open: false, dateKey: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedDays, setExpandedDays] = useState({});

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/bookings/my', {
        params: { startDate, endDate },
      });
      setBookings(response.data);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Sync calendar month → date range
  useEffect(() => {
    if (viewMode === 'calendar') {
      setStartDate(format(calendarMonth, 'yyyy-MM-dd'));
      setEndDate(format(endOfMonth(calendarMonth), 'yyyy-MM-dd'));
    }
  }, [calendarMonth, viewMode]);

  const handleViewModeChange = (_, newMode) => {
    if (!newMode) return;
    setViewMode(newMode);
    if (newMode === 'calendar') {
      setStartDate(format(calendarMonth, 'yyyy-MM-dd'));
      setEndDate(format(endOfMonth(calendarMonth), 'yyyy-MM-dd'));
    }
  };

  const groupedBookings = useMemo(() => {
    const groups = {};
    bookings
      .sort((a, b) => new Date(a.date || a.booking_date) - new Date(b.date || b.booking_date))
      .forEach((booking) => {
        const dateStr = booking.date || booking.booking_date;
        const dateKey = dateStr.split('T')[0];
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(booking);
      });
    return groups;
  }, [bookings]);

  const sortedDates = Object.keys(groupedBookings).sort();

  const isUpcoming = (dateStr) => new Date(dateStr) >= new Date(format(new Date(), 'yyyy-MM-dd'));

  const toggleDay = (dateKey) => {
    setExpandedDays((prev) => ({ ...prev, [dateKey]: !prev[dateKey] }));
  };

  const handleRelease = async () => {
    const { booking } = releaseDialog;
    if (!booking) return;
    try {
      await api.delete(`/bookings/${booking._id || booking.id}`);
      setSnackbar({ open: true, message: `Seat ${booking.seat_number || booking.seatNumber} released!`, severity: 'success' });
      setReleaseDialog({ open: false, booking: null });
      fetchBookings();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to release.', severity: 'error' });
    }
  };

  const handleReleaseDay = async () => {
    const { bookingIds } = releaseDayDialog;
    if (!bookingIds || bookingIds.length === 0) return;
    try {
      await api.delete('/bookings/bulk', { data: { booking_ids: bookingIds } });
      setSnackbar({ open: true, message: `Released ${bookingIds.length} seat(s) for the day!`, severity: 'success' });
      setReleaseDayDialog({ open: false, date: null, bookingIds: [] });
      setDayDetailDialog({ open: false, dateKey: null });
      fetchBookings();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to release.', severity: 'error' });
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr + 'T00:00:00'), 'EEEE, dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const viewOnFloorPlan = (dateStr) => navigate(`/?mode=individual&date=${dateStr}`);

  const navigateMonth = (delta) =>
    setCalendarMonth((prev) => (delta > 0 ? addMonths(prev, 1) : subMonths(prev, 1)));

  const calendarGrid = useMemo(() => buildCalendarGrid(calendarMonth), [calendarMonth]);

  const dayDetailBookings = dayDetailDialog.dateKey ? (groupedBookings[dayDetailDialog.dateKey] || []) : [];

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3, color: '#052e2b' }}>
        My Bookings
      </Typography>

      {/* Filter bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small">
            <ToggleButton value="list">
              <ViewListIcon sx={{ mr: 0.5 }} fontSize="small" /> List
            </ToggleButton>
            <ToggleButton value="calendar">
              <CalendarMonthIcon sx={{ mr: 0.5 }} fontSize="small" /> Calendar
            </ToggleButton>
          </ToggleButtonGroup>

          {viewMode === 'list' && (
            <>
              <TextField
                label="From"
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 180 }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 180 }}
              />
              <Button
                variant="contained"
                onClick={fetchBookings}
                sx={{ backgroundColor: '#052e2b', '&:hover': { backgroundColor: '#0a4a45' } }}
              >
                Filter
              </Button>
            </>
          )}

          <Chip label={`${bookings.length} booking(s)`} color="primary" variant="outlined" />
          <Chip label={`${sortedDates.length} day(s)`} variant="outlined" />
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'list' ? (
        /* ── LIST VIEW ── */
        sortedDates.length === 0 ? (
          <Paper sx={{ textAlign: 'center', p: 4 }}>
            <Typography color="text.secondary">No bookings found for the selected date range.</Typography>
          </Paper>
        ) : (
          sortedDates.map((dateKey) => {
            const dayBookings = groupedBookings[dateKey];
            const upcoming = isUpcoming(dateKey);
            const expanded = expandedDays[dateKey] !== false;
            const bookingIds = dayBookings.map((b) => b._id || b.id);

            return (
              <Paper key={dateKey} sx={{ mb: 1.5, overflow: 'hidden' }}>
                <Box
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    backgroundColor: upcoming ? '#e8f5e9' : '#f5f5f5',
                    '&:hover': { backgroundColor: upcoming ? '#c8e6c9' : '#eeeeee' },
                  }}
                  onClick={() => toggleDay(dateKey)}
                >
                  <IconButton size="small">
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1, color: '#052e2b' }}>
                    {formatDate(dateKey)}
                  </Typography>
                  <Chip
                    label={`${dayBookings.length} seat(s)`}
                    size="small"
                    sx={{ backgroundColor: '#052e2b', color: 'white' }}
                  />
                  {upcoming && <Chip label="Upcoming" size="small" color="success" />}
                  {!upcoming && <Chip label="Past" size="small" color="default" />}
                  <Tooltip title="View on Floor Plan">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); viewOnFloorPlan(dateKey); }}
                      sx={{ color: '#052e2b' }}
                    >
                      <MapIcon />
                    </IconButton>
                  </Tooltip>
                  {upcoming && (
                    <Tooltip title="Release all seats for this day">
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setReleaseDayDialog({ open: true, date: dateKey, bookingIds });
                        }}
                        sx={{ ml: 0.5 }}
                      >
                        Release All
                      </Button>
                    </Tooltip>
                  )}
                </Box>
                <Collapse in={expanded}>
                  <Box sx={{ p: 1.5, pt: 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {dayBookings.map((booking) => {
                      const seatNum = booking.seat_number || booking.seatNumber;
                      const zone = getZone(seatNum);
                      return (
                        <Chip
                          key={booking._id || booking.id}
                          icon={<EventSeatIcon />}
                          label={`Seat ${seatNum} (${zone})`}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: '#052e2b', color: '#052e2b' }}
                          onDelete={upcoming ? () => setReleaseDialog({ open: true, booking }) : undefined}
                          deleteIcon={upcoming ? <DeleteIcon fontSize="small" /> : undefined}
                        />
                      );
                    })}
                  </Box>
                </Collapse>
              </Paper>
            );
          })
        )
      ) : (
        /* ── CALENDAR VIEW ── */
        <Paper sx={{ p: 2 }}>
          {/* Month navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, gap: 2 }}>
            <IconButton onClick={() => navigateMonth(-1)}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#052e2b', minWidth: 180, textAlign: 'center' }}>
              {format(calendarMonth, 'MMMM yyyy')}
            </Typography>
            <IconButton onClick={() => navigateMonth(1)}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Day-of-week headers */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
            {DAYS_OF_WEEK.map((d) => (
              <Box key={d} sx={{ textAlign: 'center', py: 0.5 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">{d}</Typography>
              </Box>
            ))}
          </Box>

          {/* Calendar grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {calendarGrid.map((day, idx) => {
              if (!day) return <Box key={idx} />;
              const dateKey = format(day, 'yyyy-MM-dd');
              const dayBookings = groupedBookings[dateKey] || [];
              const hasBookings = dayBookings.length > 0;
              const upcoming = isUpcoming(dateKey);
              const today = isToday(day);

              return (
                <Box
                  key={dateKey}
                  onClick={() => hasBookings && setDayDetailDialog({ open: true, dateKey })}
                  sx={{
                    border: today ? '2px solid #052e2b' : '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 0.75,
                    minHeight: 72,
                    cursor: hasBookings ? 'pointer' : 'default',
                    backgroundColor: hasBookings
                      ? upcoming ? '#e8f5e9' : '#f5f5f5'
                      : 'white',
                    '&:hover': hasBookings
                      ? { backgroundColor: upcoming ? '#c8e6c9' : '#eeeeee' }
                      : {},
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={today ? 700 : 400}
                    sx={{ color: today ? '#052e2b' : 'text.primary', alignSelf: 'flex-start' }}
                  >
                    {format(day, 'd')}
                  </Typography>
                  {hasBookings && (
                    <Chip
                      icon={<EventSeatIcon style={{ fontSize: 12 }} />}
                      label={`${dayBookings.length}`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        backgroundColor: '#052e2b',
                        color: 'white',
                        '& .MuiChip-icon': { color: 'white' },
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: '#e8f5e9', border: '1px solid #e0e0e0', borderRadius: 0.5 }} />
              <Typography variant="caption">Upcoming booking</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 0.5 }} />
              <Typography variant="caption">Past booking</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, border: '2px solid #052e2b', borderRadius: 0.5 }} />
              <Typography variant="caption">Today</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Day detail dialog (from calendar click) */}
      <Dialog
        open={dayDetailDialog.open}
        onClose={() => setDayDetailDialog({ open: false, dateKey: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#052e2b', fontWeight: 600 }}>
          {dayDetailDialog.dateKey && formatDate(dayDetailDialog.dateKey)}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {dayDetailBookings.map((booking) => {
              const seatNum = booking.seat_number || booking.seatNumber;
              const zone = getZone(seatNum);
              const upcoming = dayDetailDialog.dateKey && isUpcoming(dayDetailDialog.dateKey);
              return (
                <Chip
                  key={booking._id || booking.id}
                  icon={<EventSeatIcon />}
                  label={`Seat ${seatNum} (${zone})`}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: '#052e2b', color: '#052e2b' }}
                  onDelete={
                    upcoming
                      ? () => {
                          setDayDetailDialog({ open: false, dateKey: null });
                          setReleaseDialog({ open: true, booking });
                        }
                      : undefined
                  }
                  deleteIcon={upcoming ? <DeleteIcon fontSize="small" /> : undefined}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          {dayDetailDialog.dateKey && isUpcoming(dayDetailDialog.dateKey) && (
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={() => {
                const bookingIds = dayDetailBookings.map((b) => b._id || b.id);
                setDayDetailDialog({ open: false, dateKey: null });
                setReleaseDayDialog({ open: true, date: dayDetailDialog.dateKey, bookingIds });
              }}
            >
              Release All
            </Button>
          )}
          <Tooltip title="View on Floor Plan">
            <Button
              variant="outlined"
              startIcon={<MapIcon />}
              onClick={() => {
                viewOnFloorPlan(dayDetailDialog.dateKey);
                setDayDetailDialog({ open: false, dateKey: null });
              }}
              sx={{ color: '#052e2b', borderColor: '#052e2b' }}
            >
              Floor Plan
            </Button>
          </Tooltip>
          <Button onClick={() => setDayDetailDialog({ open: false, dateKey: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Release single booking dialog */}
      <Dialog open={releaseDialog.open} onClose={() => setReleaseDialog({ open: false, booking: null })}>
        <DialogTitle>Release Booking</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Release Seat {releaseDialog.booking?.seat_number || releaseDialog.booking?.seatNumber} on{' '}
            {releaseDialog.booking && formatDate((releaseDialog.booking.date || releaseDialog.booking.booking_date).split('T')[0])}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseDialog({ open: false, booking: null })}>Cancel</Button>
          <Button onClick={handleRelease} color="error" variant="contained">Release</Button>
        </DialogActions>
      </Dialog>

      {/* Release all for day dialog */}
      <Dialog open={releaseDayDialog.open} onClose={() => setReleaseDayDialog({ open: false, date: null, bookingIds: [] })}>
        <DialogTitle>Release All Bookings</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Release all {releaseDayDialog.bookingIds.length} seat(s) for{' '}
            {releaseDayDialog.date && formatDate(releaseDayDialog.date)}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseDayDialog({ open: false, date: null, bookingIds: [] })}>Cancel</Button>
          <Button onClick={handleReleaseDay} color="error" variant="contained">Release All</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MyBookings;
